import dayjs, { Dayjs } from "dayjs";
import { EMPTY_STR } from "@/constants/string";

export type TPartnerFilterFormExtended = {
  managementNumber: string[];
  partnerName: string[];
  nationalityId: string[];
  prefecture: string[];
  city: string[];
  isOverseas: boolean;
  location: string[];
  contactPerson: string[];
  registrationDateFrom: Dayjs | null;
  registrationDateTo: Dayjs | null;
};

export type TPartnerDisplayColumnSetting = {
  id: string;
  label: string;
  visible: boolean;
  order: number;
};

export const FILTER_LABEL_MAX_LENGTH = 30;

export const PARTNER_LOCKED_COLUMN_IDS = ["partnerName"];

export const PARTNER_DATE_RANGE_KEY = "registrationDate";

export const defaultPartnerFilterValues: TPartnerFilterFormExtended = {
  managementNumber: [],
  partnerName: [],
  nationalityId: [],
  prefecture: [],
  city: [],
  isOverseas: false,
  location: [],
  contactPerson: [],
  registrationDateFrom: null,
  registrationDateTo: null,
};

export const defaultPartnerDisplayColumns: TPartnerDisplayColumnSetting[] = [
  { id: "managementNumber", label: "パートナー管理番号", visible: true, order: 1 },
  { id: "partnerName", label: "パートナー名", visible: true, order: 2 },
  { id: "nationality", label: "国籍", visible: true, order: 3 },
  { id: "location", label: "所在地", visible: true, order: 4 },
  { id: "contactPerson", label: "担当者", visible: true, order: 5 },
  { id: "createdAt", label: "登録日", visible: true, order: 6 },
];

export const PARTNER_FILTER_FIELD_LABELS: { key: keyof TPartnerFilterFormExtended; label: string }[] = [
  { key: "managementNumber", label: "パートナー管理番号" },
  { key: "partnerName", label: "パートナー名" },
  { key: "nationalityId", label: "国籍" },
  { key: "prefecture", label: "所在地（都道府県）" },
  { key: "city", label: "所在地（市区町村）" },
  { key: "location", label: "所在地" },
  { key: "contactPerson", label: "担当者" },
];

export const PARTNER_DATE_RANGE_FIELD = {
  fromKey: "registrationDateFrom" as const,
  toKey: "registrationDateTo" as const,
  label: "登録日",
};

// Normalize date fields from strings (JSON) to Dayjs objects
export const normalizePartnerDateFields = (filters: Partial<TPartnerFilterFormExtended>): Partial<TPartnerFilterFormExtended> => {
  const result = { ...filters };
  if (result.registrationDateFrom && typeof result.registrationDateFrom === "string") {
    const day = dayjs(result.registrationDateFrom);
    result.registrationDateFrom = day.isValid() ? day : null;
  }
  if (result.registrationDateTo && typeof result.registrationDateTo === "string") {
    const day = dayjs(result.registrationDateTo);
    result.registrationDateTo = day.isValid() ? day : null;
  }
  return result;
};

// Serialize title + filters for dirty-checking comparison
export const serializePartnerForComparison = (title: string, filters: TPartnerFilterFormExtended): string => {
  const normalized = { ...filters };
  if (normalized.registrationDateFrom) {
    normalized.registrationDateFrom = dayjs(normalized.registrationDateFrom).format("YYYY-MM-DD") as unknown as typeof normalized.registrationDateFrom;
  }
  if (normalized.registrationDateTo) {
    normalized.registrationDateTo = dayjs(normalized.registrationDateTo).format("YYYY-MM-DD") as unknown as typeof normalized.registrationDateTo;
  }
  return JSON.stringify({ title, filters: normalized });
};

/** Check if a filter value is empty (empty string, null, undefined, or empty array) */
export const isEmptyPartnerFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export const stripEmptyPartnerFilterFields = (filters: TPartnerFilterFormExtended): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyPartnerFilterValue(value)) continue;
    if (key === "isOverseas" && value === false) continue;
    result[key] = value;
  }
  return result;
};

/** Multi-select filter field keys */
export const PARTNER_MULTI_SELECT_FILTER_KEYS: (keyof TPartnerFilterFormExtended)[] = [
  "managementNumber",
  "partnerName",
  "nationalityId",
  "prefecture",
  "city",
  "location",
  "contactPerson",
];

/** Normalize legacy scalar filter values to arrays for backward compatibility with saved conditions */
export const normalizePartnerMultiSelectFields = (filters: Partial<Record<string, unknown>>): Partial<TPartnerFilterFormExtended> => {
  const result = { ...filters };
  // Backward compatibility for conditions saved while this checkbox used 0/1.
  if (result.isOverseas !== undefined) {
    result.isOverseas = Boolean(result.isOverseas);
  }
  for (const key of PARTNER_MULTI_SELECT_FILTER_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) continue;
    if (value === "" || value === null || value === 0) {
      result[key] = [];
    } else {
      result[key] = [String(value)];
    }
  }
  return result as Partial<TPartnerFilterFormExtended>;
};

export const formatPartnerDateValue = (value: Dayjs | string | null): string => {
  if (!value) return EMPTY_STR.DATE;
  const day = dayjs(value);
  return day.isValid() ? day.format("YYYY/MM/DD") : EMPTY_STR.DATE;
};
